/**
 * DesignDocEditor — the creator-facing editor for a {@link GameDesign} doc
 * (the mechanics authority). Used on the wizard's review step and on the
 * Settings tab (post-build design edits). Fully controlled: every keystroke
 * flows up via `onChange`; the PARENT live-validates with validateGameDesign
 * and passes the first error down — the editor renders drafts, it never
 * blocks them.
 */
import type {
  ConceptAnalysisMetadata,
  GameDesign,
} from '@djgg/creations';
import { DESIGN_LIMITS } from '@djgg/creations';
import { useState } from 'react';

export interface DesignDocEditorProps {
  design: GameDesign;
  onChange: (design: GameDesign) => void;
  /** First validateGameDesign error for the current draft ('' / undefined = valid). */
  error?: string;
  disabled?: boolean;
  /** Proposal baseline + provenance classify creator changes without mutating. */
  baseline?: GameDesign | null;
  metadata?: ConceptAnalysisMetadata | null;
}

export function DesignDocEditor({
  design,
  onChange,
  error,
  disabled,
  baseline,
  metadata,
}: DesignDocEditorProps) {
  const [verbDraft, setVerbDraft] = useState('');

  const patch = (part: Partial<GameDesign>) => {
    const next = { ...design, ...part };
    onChange(next);
  };

  const addVerb = () => {
    const verb = verbDraft.trim().toLowerCase();
    setVerbDraft('');
    if (!verb || design.coreVerbs.includes(verb)) return;
    if (design.coreVerbs.length >= DESIGN_LIMITS.coreVerbsMax) return;
    patch({ coreVerbs: [...design.coreVerbs, verb] });
  };

  return (
    <div className="stack" data-testid="design-editor" style={{ gap: 12 }}>
      <div className="field">
        <label className="field__label" htmlFor="design-summary">
          What the game is
        </label>
        <textarea
          id="design-summary"
          className="textarea"
          rows={2}
          maxLength={DESIGN_LIMITS.summaryMax}
          value={design.summary}
          disabled={disabled}
          onChange={(e) => patch({ summary: e.target.value })}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="design-verb-input">
          Core verbs — what the player DOES
        </label>
        <div className="chips" data-testid="design-verbs">
          {design.coreVerbs.map((verb) => (
            <button
              type="button"
              key={verb}
              className="chip chip--active"
              title="Remove"
              disabled={disabled}
              onClick={() => patch({ coreVerbs: design.coreVerbs.filter((v) => v !== verb) })}
            >
              {verb} ×
            </button>
          ))}
          <input
            id="design-verb-input"
            className="input"
            style={{ maxWidth: 160 }}
            placeholder="add a verb…"
            maxLength={DESIGN_LIMITS.coreVerbMaxLen}
            value={verbDraft}
            disabled={disabled}
            onChange={(e) => setVerbDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addVerb();
              }
            }}
            onBlur={addVerb}
          />
        </div>
      </div>

      <div className="field" data-testid="design-mechanics">
        <label className="field__label">Mechanics — the binding rules</label>
        {design.mechanics.map((rule, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <textarea
              className="textarea"
              rows={1}
              style={{ flex: 1 }}
              maxLength={DESIGN_LIMITS.mechanicMaxLen}
              value={rule}
              disabled={disabled}
              onChange={(e) =>
                patch({ mechanics: design.mechanics.map((m, j) => (j === i ? e.target.value : m)) })
              }
            />
            <button
              type="button"
              className="btn btn--sm"
              title="Remove rule"
              disabled={disabled || design.mechanics.length <= 1}
              onClick={() => patch({ mechanics: design.mechanics.filter((_, j) => j !== i) })}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn--sm"
          disabled={disabled || design.mechanics.length >= DESIGN_LIMITS.mechanicsMax}
          data-testid="design-add-mechanic"
          onClick={() => patch({ mechanics: [...design.mechanics, ''] })}
        >
          + Add rule
        </button>
      </div>

      <div className="field" data-testid="design-entities">
        <label className="field__label">Entities — what the game world contains</label>
        {design.entities.map((entity, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input
              className="input"
              style={{ maxWidth: 160 }}
              placeholder="name"
              maxLength={DESIGN_LIMITS.entityNameMaxLen}
              value={entity.name}
              disabled={disabled}
              onChange={(e) =>
                patch({
                  entities: design.entities.map((en, j) =>
                    j === i ? { ...en, name: e.target.value } : en,
                  ),
                })
              }
            />
            <input
              className="input"
              style={{ flex: 1 }}
              placeholder="what it does / why it exists"
              maxLength={DESIGN_LIMITS.entityRoleMaxLen}
              value={entity.role}
              disabled={disabled}
              onChange={(e) =>
                patch({
                  entities: design.entities.map((en, j) =>
                    j === i ? { ...en, role: e.target.value } : en,
                  ),
                })
              }
            />
            <button
              type="button"
              className="btn btn--sm"
              title="Remove entity"
              disabled={disabled || design.entities.length <= 1}
              onClick={() => patch({ entities: design.entities.filter((_, j) => j !== i) })}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn--sm"
          disabled={disabled || design.entities.length >= DESIGN_LIMITS.entitiesMax}
          data-testid="design-add-entity"
          onClick={() => patch({ entities: [...design.entities, { name: '', role: '' }] })}
        >
          + Add entity
        </button>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="design-controls">
          Controls — what the inputs do
        </label>
        <input
          id="design-controls"
          className="input"
          maxLength={DESIGN_LIMITS.controlsMax}
          value={design.controls}
          disabled={disabled}
          onChange={(e) => patch({ controls: e.target.value })}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="design-win">
          Win condition
        </label>
        <input
          id="design-win"
          className="input"
          maxLength={DESIGN_LIMITS.winConditionMax}
          value={design.winCondition}
          disabled={disabled}
          onChange={(e) => patch({ winCondition: e.target.value })}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="design-mode-notes">
          Mode notes <span className="muted">(optional)</span>
        </label>
        <input
          id="design-mode-notes"
          className="input"
          maxLength={DESIGN_LIMITS.modeNotesMax}
          value={design.modeNotes ?? ''}
          disabled={disabled}
          onChange={(e) => patch({ modeNotes: e.target.value || undefined })}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="design-theme">
          Theme &amp; visual direction <span className="muted">(optional, non-binding)</span>
        </label>
        <input
          id="design-theme"
          className="input"
          maxLength={DESIGN_LIMITS.themeMax}
          value={design.theme ?? ''}
          disabled={disabled}
          onChange={(e) => patch({ theme: e.target.value || undefined })}
        />
      </div>

      {error && (
        <div className="field__error" data-testid="design-error">
          {error}
        </div>
      )}
    </div>
  );
}
